import { ForecastFolder, UserArpsItem } from "models/UserArpsModel";

import { ICheckedForecast } from "store/features";

export const getNewTypeWellName = (typeWells: UserArpsItem[]) => {
  let name = "New Type Well";
  const existingNames = {};
  for (const item of typeWells) {
    existingNames[item.title] = true;
  }
  let number = 1;
  while (existingNames[name]) {
    name = `New Type Well (${number++})`;
  }
  return name;
};

/**
 * Finds the folder that contains a specific UserArpsItem
 * @param typeWell - The UserArpsItem to find the folder for
 * @param folders - Array of ForecastFolder objects to search through
 * @returns The ForecastFolder containing the typeWell, or null if not found
 */
export const findFolderForTypeWell = (
  typeWell: UserArpsItem,
  folders: ForecastFolder[]
): ForecastFolder | null => {
  if (!typeWell || !folders) {
    return null;
  }

  for (const folder of folders) {
    if (folder.folderId === typeWell.folderId) {
      return folder;
    }

    if (
      folder.children &&
      folder.children.some(
        (child) =>
          child.id === typeWell.id ||
          child.key === typeWell.key ||
          child.uniqueID === typeWell.uniqueID
      )
    ) {
      return folder;
    }
  }

  return null;
};

/**
 * Gets the next available "Untitled Type Well" name in the same folder
 * @param typeWell - The UserArpsItem to find the folder for
 * @param folders - Array of ForecastFolder objects to search through
 * @returns The next available "Untitled Type Well" name with appropriate numbering
 */
export const getUntitledTypeWellName = (
  typeWell: UserArpsItem,
  folders: ForecastFolder[]
): string => {
  const parentFolder = findFolderForTypeWell(typeWell, folders);

  if (!parentFolder || !parentFolder.children) {
    return "Untitled Type Well";
  }

  const existingNames = {};
  parentFolder.children.forEach((child) => {
    if (!child.isFolder && child.title) {
      existingNames[child.title] = true;
    }
  });

  let suggestedName = "Untitled Type Well";
  let number = 1;

  while (existingNames[suggestedName]) {
    suggestedName = `Untitled Type Well (${number++})`;
  }

  return suggestedName;
};

/**
 * EVA-6256: after a folders refetch (e.g. an upload), returns the freshly-fetched version of
 * the currently open type well so the editor's snapshot can be re-synced to the latest data.
 * Returns null when there is no open well, the user has unsaved edits (which must not be
 * clobbered), or the well is no longer present in the refreshed folders.
 */
export const getRefreshedTypeWell = (
  folders: ForecastFolder[],
  openTypeWell: Nullable<UserArpsItem>,
  hasUnsavedChanges: boolean
): UserArpsItem | null => {
  if (!openTypeWell || hasUnsavedChanges || !folders) {
    return null;
  }

  return (
    folders
      .flatMap((folder) => folder.children || [])
      .find((typeWell) => typeWell.id === openTypeWell.id) ?? null
  );
};

/**
 * EVA-6256: the plotted type-well curve is built from the checked-type-well snapshots
 * (checkedGlobalTypeWells / checkedTypeWells), whose arps/constants are frozen at check time
 * and aren't refreshed by a folders refetch. After an upload, replace the matching entry's
 * arps/constants/wellData with the refreshed values so the curve re-plots. Returns a NEW array
 * (required for the chart series to recompute) only when the well is currently checked;
 * otherwise the same array is returned unchanged (nothing plotted, nothing to refresh).
 */
export const refreshCheckedTypeWell = (
  checked: ICheckedForecast[],
  freshTypeWell: UserArpsItem
): ICheckedForecast[] => {
  if (!checked) {
    return checked;
  }
  const index = checked.findIndex(
    (tw) => tw.id === freshTypeWell.id || tw.id === freshTypeWell.key
  );
  if (index === -1) {
    return checked;
  }
  const next = [...checked];
  next[index] = {
    ...next[index],
    arps: freshTypeWell.arps,
    constants: freshTypeWell.constants,
    wellData: freshTypeWell.wellData
  };
  return next;
};

export interface TypeWellUploadResync {
  selectedTypeWell: UserArpsItem;
  checkedGlobalTypeWells: ICheckedForecast[];
  checkedTypeWells: ICheckedForecast[];
}

/**
 * EVA-6256: after an upload + folders refetch, compute the store updates needed to reflect the
 * uploaded type well across BOTH the editor (selectedTypeWell) and the plotted curve (the
 * checked-type-well snapshots). A folders refetch alone leaves all of these stale until the
 * well is re-selected. Returns null when there is nothing to resync — no open well, unsaved
 * edits in flight (which must not be clobbered), or the well is gone from the refreshed folders.
 */
export const getTypeWellUploadResync = (
  folders: ForecastFolder[],
  openTypeWell: Nullable<UserArpsItem>,
  hasUnsavedChanges: boolean,
  checkedGlobalTypeWells: ICheckedForecast[],
  checkedTypeWells: ICheckedForecast[]
): TypeWellUploadResync | null => {
  const freshTypeWell = getRefreshedTypeWell(folders, openTypeWell, hasUnsavedChanges);
  if (!freshTypeWell) {
    return null;
  }
  return {
    selectedTypeWell: freshTypeWell,
    checkedGlobalTypeWells: refreshCheckedTypeWell(checkedGlobalTypeWells, freshTypeWell),
    checkedTypeWells: refreshCheckedTypeWell(checkedTypeWells, freshTypeWell)
  };
};
